import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, interval, of } from 'rxjs';
import {
  catchError,
  delay,
  distinctUntilChanged,
  groupBy,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  OBJECT_CARD_KINDS,
  ObjectCardGeneralType,
  ObjectCardType,
} from '@common/components/object-card';
import { FeatureFlagsService, FlagsKeys } from '@services/feature-flags.service';
import { ObjectsService } from '@services/objects.service';
import { storeCurrentUser } from '@store/actions/current_user.actions';
import {
  addMonitoringItem,
  batchSyncMonitoring,
  createMonitoringItemFromRawObject,
  loadMonitoring,
  loadMonitoringFailure,
  loadMonitoringSuccess,
  loadObjectCardData,
  loadObjectCardDataFailure,
  loadObjectCardDataSuccess,
  normalizeMonitoringFromObjects,
  patchShowOnMap,
  pollMonitoringSuccess,
  reloadOpenedObjectCards,
  setMaxRt,
  setMultipleShowOnMap,
  syncMonitoringFromSocket,
  updateMonitoringFromRawObject,
  updateMultipleMonitoring,
  updateOneMonitoring,
} from '@store/monitoring/monitoring.actions';
import { getCoordinateKey } from '@store/monitoring/monitoring.reducer';
import {
  selectAllMonitoring,
  selectMaxRt,
  selectMonitoringEntities,
  selectMonitoringIds,
  selectObjectCards,
  selectVisibleIds,
} from '@store/monitoring/monitoring.selectors';
import { selectCurrenUserVisibleObjectsCount } from '@store/selectors/current_user.selectors';
import { selectObjects } from '@store/selectors/objects.selectors';

import { MonitoringService } from '../../app/portal/pages/monitoring-page/monitoring.service';
import {
  MonitoringItem,
  MonitoringItemTemplateParams,
} from '../../app/portal/pages/monitoring-page/monitoring.types';

const DEFAULT_POLLING_INTERVAL_MS = 5000;

@Injectable()
export class MonitoringEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly monitoringService = inject(MonitoringService);
  private readonly objectsService = inject(ObjectsService);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  loadMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMonitoring),
      concatLatestFrom(() => this.store.select(selectCurrenUserVisibleObjectsCount)),
      mergeMap(([_, totalItems]) => {
        if (!totalItems) {
          return of(loadMonitoringSuccess({ monitoring: [] }));
        }

        return this.monitoringService.monitoringListPaginated(totalItems, 1000).pipe(
          // return this.monitoringService.monitoringList().pipe(
          map((monitoring) => loadMonitoringSuccess({ monitoring })),
          catchError((error) => of(loadMonitoringFailure({ error: error.message }))),
        );
      }),
    ),
  );

  createMonitoringItemFromRawObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createMonitoringItemFromRawObject),
      map(({ rawObject }) => {
        const item = this.mapObjectToMonitoringItem(rawObject);
        return addMonitoringItem({ item });
      }),
    ),
  );

  updateMonitoringFromRawObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMonitoringFromRawObject),
      concatLatestFrom(() => this.store.select(selectMonitoringEntities)),
      filter(([{ rawObject }, entities]) => !!entities[rawObject.id]),
      map(([{ rawObject }, entities]) => {
        const previousItem = entities[rawObject.id];
        const fullItem = this.mapObjectToMonitoringItem(rawObject, previousItem);

        return updateOneMonitoring({
          id: fullItem.id,
          changes: fullItem,
        });
      }),
    ),
  );

  patchShowOnMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patchShowOnMap),
      withLatestFrom(this.store.select(selectVisibleIds)),
      map(([{ objectIds, state }, visibleIds]) => {
        const visibleSet = new Set(visibleIds);

        const updates = objectIds
          .filter((id) => visibleSet.has(id) !== state)
          .map((id) => ({ id, show: state }));

        return updates.length > 0
          ? setMultipleShowOnMap({ updates })
          : { type: '[Monitoring] Skip setMultipleShowOnMap' };
      }),
    ),
  );

  syncMonitoringFromSocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncMonitoringFromSocket),
      concatLatestFrom(() => this.store.select(selectMonitoringEntities)),
      switchMap(([{ newObjectsIds, removedObjectIds }, monitoringEntities]) => {
        const toDelete = removedObjectIds.filter((id) => !!monitoringEntities[id]);

        const fetch$ =
          newObjectsIds.length > 0
            ? this.monitoringService.getMonitoringByIds(newObjectsIds)
            : of([]);

        return fetch$.pipe(
          map((objects: MonitoringItem[]) => {
            return batchSyncMonitoring({
              items: objects,
              deleteIds: toDelete,
            });
          }),
        );
      }),
    ),
  );

  updateMonitoringItems$ = createEffect(() =>
    interval(60000).pipe(
      withLatestFrom(this.store.select(selectAllMonitoring)),
      switchMap(([_, monitoringItems]) => {
        const updates = monitoringItems
          .map((item) => {
            const updatedSatellitesCount = this.monitoringService.getParamsForSatellitesCount(
              item.lastMessage,
            );
            const updatedLastActivity = this.monitoringService.getParamsForLastMessage(
              item.lastMessage,
            );

            if (
              updatedSatellitesCount?.iconPath !== item.satellitesCount?.iconPath ||
              updatedLastActivity?.iconPath !== item.lastActivity?.iconPath
            ) {
              return {
                id: item.id,
                changes: {
                  satellitesCount: updatedSatellitesCount ?? item.satellitesCount,
                  lastActivity: updatedLastActivity ?? item.lastActivity,
                },
              };
            }
            return null;
          })
          .filter(
            (
              update,
            ): update is {
              id: number;
              changes: {
                satellitesCount: MonitoringItemTemplateParams;
                lastActivity: MonitoringItemTemplateParams;
              };
            } => update !== null,
          );

        return updates.length > 0
          ? of(updateMultipleMonitoring({ updates }))
          : of({ type: '[Monitoring] No Updates Needed' });
      }),
    ),
  );

  normalizeMonitoringFromObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(normalizeMonitoringFromObjects),
      concatLatestFrom(() => [
        this.store.select(selectMonitoringEntities),
        this.store.select(selectObjects),
      ]),
      map(([{ ids }, monitoringEntities, objects]) => {
        const idSet = new Set(ids);

        // 1. Найти недостающие ID
        const missingIds = ids.filter((id) => !monitoringEntities[id]);

        // 2. Создать Map объектов по ID
        const objectsMap = new Map(objects.map((obj: any) => [obj.id, obj]));

        // 3. Преобразовать недостающие объекты в MonitoringItem[]
        const itemsToAdd: MonitoringItem[] = missingIds.reduce((acc, id) => {
          const obj = objectsMap.get(id);
          if (!obj) return acc;
          acc.push(this.mapObjectToMonitoringItem(obj));
          return acc;
        }, [] as MonitoringItem[]);

        // 4. Найти лишние ID в monitoring
        const idsToDelete = Object.keys(monitoringEntities)
          .map(Number)
          .filter((id) => !idSet.has(id));

        return batchSyncMonitoring({
          items: itemsToAdd,
          deleteIds: idsToDelete,
        });
      }),
    ),
  );

  /**
   * Карточки разных объектов (и разных типов одного объекта) грузятся параллельно,
   * повторный запрос той же карточки отменяет предыдущий.
   */
  loadObjectCardData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadObjectCardData),
      groupBy(({ objectId, cardType }) => `${cardType}:${objectId}`),
      mergeMap((group$) =>
        group$.pipe(
          switchMap(({ objectId, cardType }) =>
            this.objectsService.getObjectById(objectId, false, true, cardType).pipe(
              map((objectCardInfo) => loadObjectCardDataSuccess({ cardType, objectCardInfo })),
              catchError((error) =>
                of(
                  loadObjectCardDataFailure({
                    objectId,
                    cardType,
                    error: error?.message || 'Unknown error',
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  /** Данные карточки свежее списка мониторинга — подтягиваем в список имя, иконку, водителей, прицепы */
  loadObjectCardDataSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadObjectCardDataSuccess),
      concatLatestFrom(() => this.store.select(selectMonitoringEntities)),
      filter(([{ objectCardInfo }, entities]) => !!entities[objectCardInfo.id]),
      map(([{ objectCardInfo }, entities]) => ({
        id: objectCardInfo.id,
        changes: this.getMonitoringChangesFromCard(
          objectCardInfo,
          entities[objectCardInfo.id] as MonitoringItem,
        ),
      })),
      filter(({ changes }) => Object.keys(changes).length > 0),
      map(({ id, changes }) => updateOneMonitoring({ id, changes })),
    ),
  );

  /**
   * Настройки карточек в профиле изменились (сохранение профиля, сокет current_user_refresh).
   * Бэкенд фильтрует блоки карточки по этим настройкам, поэтому одной перерисовки мало —
   * открытые карточки надо перезапросить.
   */
  reloadObjectCardsOnSettingsChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(storeCurrentUser),
      map(({ currentUser }) =>
        JSON.stringify([currentUser?.objectCardSettings, currentUser?.objectCardFixed]),
      ),
      distinctUntilChanged(),
      map(() => reloadOpenedObjectCards()),
    ),
  );

  /** Перезапрашивает все карточки, данные которых сейчас лежат в сторе */
  reloadOpenedObjectCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reloadOpenedObjectCards),
      concatLatestFrom(() => this.store.select(selectObjectCards)),
      mergeMap(([_, cards]) =>
        OBJECT_CARD_KINDS.flatMap((cardType) =>
          Object.keys(cards[cardType]).map((objectId) =>
            loadObjectCardData({ objectId: Number(objectId), cardType }),
          ),
        ),
      ),
    ),
  );

  private getMonitoringChangesFromCard(
    card: ObjectCardType,
    item: MonitoringItem,
  ): Partial<MonitoringItem> {
    const changes: Partial<MonitoringItem> = {};
    const general: Partial<ObjectCardGeneralType> = card.general ?? {};

    if (general.name && general.name !== item.name) {
      changes.name = general.name;
    }

    if (general.iconId !== undefined && general.iconId !== item.iconId) {
      changes.iconId = general.iconId;
    }

    if (general.objectIconColor !== undefined && general.objectIconColor !== item.objectIconColor) {
      changes.objectIconColor = general.objectIconColor;
    }

    if (Array.isArray(card.drivers)) {
      const driversAttached = card.drivers.length > 0;

      if (driversAttached !== item.driversAttached?.value) {
        changes.driversAttached =
          this.monitoringService.getParamsForDriversAttached(driversAttached);
      }
    }

    if (Array.isArray(card.trailers)) {
      const trailersAttached = card.trailers.length > 0;

      if (trailersAttached !== item.trailersAttached?.value) {
        changes.trailersAttached =
          this.monitoringService.getParamsForTrailersAttached(trailersAttached);
      }
    }

    return changes;
  }

  computeMaxRt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMonitoringSuccess),
      map(({ monitoring }) => {
        let maxRt: number | null = null;
        for (const item of monitoring) {
          const rt = item.lastMessage?.rt;
          if (typeof rt === 'number' && (maxRt === null || rt > maxRt)) {
            maxRt = rt;
          }
        }
        return setMaxRt({ maxRt });
      }),
    ),
  );

  private get pollingIntervalMs(): number {
    // return 1000000;
    const value: any = this.featureFlagsService.getValue(FlagsKeys.pollingInterval);
    return typeof value === 'number' && isFinite(value) && value > 0
      ? value
      : DEFAULT_POLLING_INTERVAL_MS;
  }

  pollMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setMaxRt, pollMonitoringSuccess),
      switchMap((action) =>
        of(action).pipe(
          delay(this.pollingIntervalMs),
          concatLatestFrom(() => [
            this.store.select(selectMaxRt),
            this.store.select(selectMonitoringIds),
          ]),
          switchMap(([_, maxRt, monitoringIds]) => {
            const objectIds = monitoringIds as number[];
            if (objectIds.length === 0) {
              return of({ type: '[Monitoring] Poll Skip — empty list' });
            }
            return this.monitoringService.pollMonitoringLastInfo(objectIds, maxRt).pipe(
              map(({ maxRt: newMaxRt, lastInfo }) => {
                this.store.dispatch(
                  updateMultipleMonitoring({
                    updates: Object.entries(lastInfo).map(([idStr, info]) => {
                      const id = Number(idStr);
                      return {
                        id,
                        changes: {
                          lastMessage: info.lastMessage ?? null,
                          ...(info.lastMessage ? getCoordinateKey(info.lastMessage) : {}),
                          isIgnition: this.monitoringService.getParamsForIsIgnition(
                            info.isIgnition,
                          ),
                          isMotion: this.monitoringService.getParamsForIsMotion(info.isMotion),
                          connectedStatus: this.monitoringService.getParamsForConnectedStatus(
                            info.connectedStatus,
                          ),
                          satellitesCount: this.monitoringService.getParamsForSatellitesCount(
                            info.lastMessage,
                          ),
                          lastActivity: this.monitoringService.getParamsForLastMessage(
                            info.lastMessage,
                          ),
                        },
                      };
                    }),
                  }),
                );
                return pollMonitoringSuccess({ maxRt: newMaxRt, lastInfo });
              }),
              catchError((error) => {
                console.error('[Monitoring] Poll error:', error);
                return of(setMaxRt({ maxRt }));
              }),
            );
          }),
        ),
      ),
    ),
  );

  private mapObjectToMonitoringItem(obj: any, previous?: MonitoringItem): MonitoringItem {
    return {
      id: obj.id,
      name: obj.name ?? 'Без имени',
      deviceCanSendCommands: obj.deviceCanSendCommands,
      uniqueId: obj.uniqueId,
      iconId: obj.iconId,
      objectIconColor: obj.objectIconColor,
      objectCaptionColor: obj.objectCaptionColor,
      trackColor: obj.trackColor,
      currentUserAccess: this.monitoringService.getCurrentUserAccessAsArray(obj.currentUserAccess),
      lastMessage: obj.lastMessage,
      accountId: obj.accountId ?? previous?.accountId,
      accountName: obj.accountName ?? previous?.accountName,
      showOnMap: previous?.showOnMap || true,
      ...getCoordinateKey(obj.lastMessage),
      isIgnition: previous?.isIgnition ?? this.monitoringService.getParamsForIsIgnition(null),
      isMotion: previous?.isMotion ?? this.monitoringService.getParamsForIsMotion(null),
      driversAttached:
        previous?.driversAttached ??
        this.monitoringService.getParamsForDriversAttached(obj.driversAttached),
      trailersAttached:
        previous?.trailersAttached ??
        this.monitoringService.getParamsForTrailersAttached(obj.trailersAttached),
      connectedStatus:
        previous?.connectedStatus ??
        this.monitoringService.getParamsForConnectedStatus(obj.connectedStatus),
      satellitesCount:
        previous?.satellitesCount ??
        this.monitoringService.getParamsForSatellitesCount(obj.lastMessage),
      lastActivity:
        previous?.lastActivity ?? this.monitoringService.getParamsForLastMessage(obj.lastMessage),
    };
  }
}
